import React, { useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import { faBars, faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { EditUserDialog, useEditUserDialog } from './EditUserDialog';
import { User } from '../../api/user';
import { useAuth } from '../../units/auth/AuthProvider';
import { getMaxAuthority } from '../../api/user-group';
import { grey } from '@mui/material/colors';

export const useUserManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const reloadUsers = () =>
        api.user
            .get({ relations: { userGroups: true, cars: true } })
            .then((ret) => ret.success && setUsers(ret.data?.sort((a, b) => (a.userName || '').localeCompare((b.userName || ''))) || []));
    const sideBar = useSideBar();
    const editUserDialog = useEditUserDialog({ ...page, reloadUsers });
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        { field: 'userId', headerName: '使用者ID', flex: 4, minWidth: 120, cellClassName: 'cell', hide: false },
        { field: 'account', headerName: '帳號', flex: 4, minWidth: 120, cellClassName: 'cell', hide: false },
        { field: 'userName', headerName: '使用者名稱', flex: 4, minWidth: 120, cellClassName: 'cell', hide: false },
        {
            field: 'userGroups',
            headerName: '使用者群組',
            flex: 5,
            minWidth: 240,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as User;
                return (
                    <>
                        {row.userGroups
                            ?.sort((a, b) => b.authority! - a.authority!)
                            .map((userGroup) => userGroup.descr)
                            .join(', ')}
                    </>
                );
            },
            valueGetter: (params: GridValueGetterParams) => {
                const row = params.row as User;
                return getMaxAuthority(row.userGroups);
            },
            hide: false,
        },
        { field: 'tel', headerName: '市話', flex: 4, minWidth: 120, cellClassName: 'cell', hide: false },
        { field: 'mobile', headerName: '手機', flex: 4, minWidth: 120, cellClassName: 'cell', hide: false },
        { field: 'email', headerName: '電子信箱', flex: 7, minWidth: 210, cellClassName: 'cell', hide: false },
        { field: 'address', headerName: '地址', flex: 7, minWidth: 210, cellClassName: 'cell', hide: false },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>操作</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as User;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            disabled={
                                ![auth.profile.userName == row.userName || auth.profile.authority > getMaxAuthority(row.userGroups)].every(
                                    (v) => v,
                                )
                            }
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editUserDialog.setUserId(row.userId || '');
                                editUserDialog.setInputAccount(row.account || '');
                                editUserDialog.setInputUserName(row.userName || '');
                                editUserDialog.setInputPassword('');
                                editUserDialog.setInputConfirmPassword('');
                                editUserDialog.setInputTel(row.tel || '');
                                editUserDialog.setInputMobile(row.mobile || '');
                                editUserDialog.setInputEmail(row.email || '');
                                editUserDialog.setInputAddress(row.address || '');
                                editUserDialog.setOriginUserGroups(row.userGroups || []);
                                editUserDialog.setInputCarIds((row.cars || []).map((car) => car.carId!));
                                editUserDialog.onOpen();
                            }}
                        >
                            編輯
                        </Button>
                        <Button
                            disabled={![auth.profile.authority > getMaxAuthority(row.userGroups)].every((v) => v)}
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.user.remove({ userId: row.userId! }).finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadUsers();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                        }
                                    },
                                })
                            }
                        >
                            刪除
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadUsers();
    }, []);
    return {
        page,
        classes,
        auth,
        users,
        setUsers,
        reloadUsers,
        sideBar,
        editUserDialog,
        inputSearchConfig,
        setInputSearchConfig,
        columns,
    };
};

export const UserManagePage = (): React.ReactElement => {
    const { page, classes, auth, users, setUsers, reloadUsers, sideBar, editUserDialog, inputSearchConfig, setInputSearchConfig, columns } =
        useUserManagePage();
    return (
        <Page {...page} title='使用者管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={inputSearchConfig}
                                onChange={(e) => setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['使用者ID', '使用者名稱', '市話', '手機', '電子信箱', '地址'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => editUserDialog.onOpen()}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增使用者'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return users
                                    .filter((v, i, a) => auth.profile.authority >= getMaxAuthority(v.userGroups))
                                    .filter((v, i, a) =>
                                        [
                                            searchs.length == 0
                                                ? true
                                                : searchs
                                                      .map(
                                                          (s) =>
                                                              v.account?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.userName?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.tel?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.mobile?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.email?.toUpperCase().includes(s.toUpperCase()) ||
                                                              v.address?.toUpperCase().includes(s.toUpperCase()),
                                                      )
                                                      .every((v) => v),
                                        ].every((v) => v),
                                    );
                            })()}
                            columns={columns}
                            getRowId={(row: User) => row.userId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditUserDialog {...editUserDialog} />
        </Page>
    );
};
